import { DeclinedAction } from "./declinedAction";
import {
  AmendedAction,
  CanceledAction,
  DiscontinuedAction,
  Order,
  ReinstatedAction,
  RejectedAction,
} from "./models";
import { OrderComponentProperty } from "./orderComponentProperty";

export interface Action {
  actionType?: Action.ActionTypeEnum;
  approvalType?: Order.ApprovalTypeEnum;
  attestation?: boolean;
  datetime?: string;
  datetimeDisplay?: string;
  declined?: boolean;
  declinedDate?: string;
  displayName?: string;
  declinedReason?: string;
  discontinueDate?: string;
  documentIds?: Array<string>;
  eSign?: boolean;
  eSignatureDate?: string;
  MARDaysOfTheWeek?: Array<string>;
  MARFrequency?: string;
  MARNotes?: string;
  MARTimes?: Array<string>;
  notes?: string;
  orderingProviderDisplayName?: string;
  orderingProviderId?: number;
  properties?: Array<OrderComponentProperty>;
  readBackAndVerified?: boolean;
  reason?: string;
  reinstateOrderId?: string;
  signatureRequests?: Action.SignatureRequestEnum[];
  timezone?: string;
  userId?: number;
  username?: string;
  // TDR-16 / §7.4 — NCI Addendum Order History fields. Carried on the PATCH /orders/{orderId}
  // body when actionType is one of:
  //   - NON_COVERED_ITEM_ADDENDUM_REPORTED  (peer, pre-existing — emitted by legacy NCI report flow)
  //   - NON_COVERED_ITEM_ADDENDUM_GENERATED (new — F1 from clinical-ui after addendum POST)
  //   - NON_COVERED_ITEM_ADDENDUM_UPDATED   (new — F1 from clinical-ui after addendum PUT)
  // Mirrors NonCoveredItemAddendum{Reported,Generated,Updated}Action in OMF's order-management.yaml.
  nciAddendumId?: number;
  // Only populated for NON_COVERED_ITEM_ADDENDUM_UPDATED (the addendum's new status name —
  // matches patient-charts AddendumStatus.name(): PENDING / DELIVERED / DECLINED / UNSIGNED /
  // SIGNED / SENT). See OMF NonCoveredItemAddendumUpdatedAction (§7.4 / wave 14).
  addendumStatus?: string;
}

export type ActionWithReason =
  | DiscontinuedAction
  | DeclinedAction
  | AmendedAction
  | CanceledAction
  | ReinstatedAction
  | RejectedAction;

export namespace Action {
  export type ActionTypeEnum =
    | "CREATED"
    | "MODIFIED"
    | "E_SIGNED"
    | "CO_SIGNED"
    | "DISCONTINUED"
    | "PRINTED"
    | "AMENDED"
    | "NOTES_ADDED"
    | "DOCUMENT_UPLOADED"
    | "CANCELED"
    | "REJECTED"
    | "MANUALLY_SIGNED"
    | "DECLINED"
    | "NON_COVERED_ITEM_ADDENDUM_REPORTED"
    | "NON_COVERED_ITEM_ADDENDUM_GENERATED"
    | "NON_COVERED_ITEM_ADDENDUM_UPDATED";
  export const ActionTypeEnum = {
    Created: "CREATED" as ActionTypeEnum,
    Modified: "MODIFIED" as ActionTypeEnum,
    ESigned: "E_SIGNED" as ActionTypeEnum,
    CoSigned: "CO_SIGNED" as ActionTypeEnum,
    Discontinued: "DISCONTINUED" as ActionTypeEnum,
    Printed: "PRINTED" as ActionTypeEnum,
    Amended: "AMENDED" as ActionTypeEnum,
    NotesAdded: "NOTES_ADDED" as ActionTypeEnum,
    DocumentUploaded: "DOCUMENT_UPLOADED" as ActionTypeEnum,
    Canceled: "CANCELED" as ActionTypeEnum,
    Rejected: "REJECTED" as ActionTypeEnum,
    ManuallySigned: "MANUALLY_SIGNED" as ActionTypeEnum,
    Declined: "DECLINED" as ActionTypeEnum,
    MAREdited: "MAR_EDITED" as ActionTypeEnum,
    // TDR-16 / §7.4 — NCI Addendum Order History action types.
    NonCoveredItemAddendumReported:
      "NON_COVERED_ITEM_ADDENDUM_REPORTED" as ActionTypeEnum,
    NonCoveredItemAddendumGenerated:
      "NON_COVERED_ITEM_ADDENDUM_GENERATED" as ActionTypeEnum,
    NonCoveredItemAddendumUpdated:
      "NON_COVERED_ITEM_ADDENDUM_UPDATED" as ActionTypeEnum,
  };
  export type SignatureRequestEnum = "created" | "discontinued";
  export const SignatureRequestEnum = {
    Created: "created" as SignatureRequestEnum,
    Discontinued: "discontinued" as SignatureRequestEnum,
  };
  export function hasReason(action: Action): boolean {
    return action.reason !== undefined || action.declinedReason !== undefined;
  }
}



// WEBPACK FOOTER //
// ./src/models/order-management/action.ts